import React, { useRef, useEffect, useLayoutEffect, useState } from "react";
import { fmtDate } from "../utils/buyerMapping";
import { n0c, n2c } from "../utils/profitFormat";
import { Segmented } from "./common/ui";
import { CH_LABELS_FALLBACK as PROFIT_CH_LABELS } from "./ProfitSettingsPanel";

// 2026-10-20: the 12-week Weekly sales trend table, moved out of CpApprovalsPage so the sidebar / Overall Summary
// Style profit lookup shows the exact same table. d = a weekly-performance response (weeks + totals).
// chargesTipHandlers(title, row) is optional (CP Approvals' charges hover); without it the cells are plain.

// 2026-10-21 Closing stock (E-com WH main floor, Planning_DB, per week) + Wks cover = closing stock / that week's net qty.
// Cover > 26 wks red, > 12 amber; "—" when net qty <= 0 or no stock figure. Total row: latest stock vs 12-week avg net qty.
export function weeksCover(stock, netQty) {
  if (stock == null || !(stock > 0) || !(netQty > 0)) return null;
  return stock / netQty;
}
const coverColor = (c) => (c == null ? "var(--text-muted)" : c > 26 ? "var(--danger)" : c > 12 ? "var(--warning, #b7791f)" : undefined);
const coverText = (c) => (c == null ? "—" : c >= 100 ? "99+" : c.toFixed(1));

// Under the Weekly Trend table (2026-10-17): which source was read, its data date + age, and muted notes for anything
// that is not in the weekly figures as-is (returns still to come, unmatched returns, returns of earlier sales, parties
// with no profit channel). d = the weekly-performance response.
export function WeeklySourceNote({ d, source }) {
  const muted = { fontSize: 11.5, color: "var(--text-muted)", marginTop: 4, maxWidth: 760 };
  if (source === "planning") return <div style={muted}>Source: Planning_DB · returns in the week they were posted<ClosingStockNote d={d} /></div>;
  const asOf = d.data_as_of?.latest_bill_date;
  const age = asOf ? Math.max(0, Math.round((new Date(new Date().toDateString()) - new Date(`${asOf}T00:00:00`)) / 86400000)) : null;
  const un = d.unlinked_returns, early = d.returns_of_earlier_sales;
  const rs = (v) => `₹${Math.round(v || 0).toLocaleString("en-IN")}`;
  const excluded = (d.excluded_parties || []).map((e) => `${e.party}: ${e.qty} (${e.reason})`).join("\n");
  return (
    <div style={muted}>
      <div title={excluded ? `Left out (not in channel scope):\n${excluded}` : undefined}>
        Source: Ginesys · data as of {asOf ? `${fmtDate(asOf)} · ${age} day${age === 1 ? "" : "s"} old` : "unknown"} · returns in {d.attribution === "return" ? "the week they were posted" : "the week of the original sale"}
      </div>
      {(d.returns_pending_weeks || []).length > 0 && <div>* Last {d.returns_pending_weeks.length} weeks: returns may still come for these sales.</div>}
      {un && un.lines > 0 && (
        <div title={(un.items || []).map((i) => `${fmtDate(i.date)} · ${i.party} · ${i.ref} · ${i.size || i.icode} · ${i.qty}`).join("\n")}>
          {un.qty} return{un.qty === 1 ? "" : "s"} ({rs(un.value)}) could not be matched to a sale - kept in the week posted.
        </div>
      )}
      {early && early.lines > 0 && <div>{early.qty} return{early.qty === 1 ? "" : "s"} ({rs(early.value)}) of sales before this window left out.</div>}
      {(d.unmapped || []).length > 0 && (
        <div>No profit channel (0 commission assumed): {d.unmapped.map((u) => `${u.party} (${u.sale_qty} sold, ${u.return_qty} returned)`).join(", ")}.</div>
      )}
      <ClosingStockNote d={d} />
    </div>
  );
}

function ClosingStockNote({ d }) {
  if (d.closing_stock_note) return <div style={{ color: "var(--warning, var(--text-muted))" }}>{d.closing_stock_note}</div>;
  if (d.totals && "closing_stock_latest" in d.totals) return <div>Closing stock: E-com WH (Planning_DB weekly snapshot, week-end Sunday){d.totals.closing_stock_week ? "" : " · no snapshot in these weeks"}.</div>;
  return null;
}

// Actual 12-week profit per net unit for the Weekly Trend Total row.
export function weeklyTotalActuals(t) {
  const profit = t?.profit_after_commission;
  return {
    perUnit: profit != null && t.net_qty > 0 ? profit / t.net_qty : null,
  };
}

// One line per channel for the charges tooltip, e.g. "Commission 25% of SP
// excl. GST · Freight ₹30/pc, not charged on returns · Avg SP ₹882 incl. 5% GST".
// Per-unit figures are the row's ₹ amount / qty, so they match the table above.
function chargeBasisText(r) {
  const b = r.basis;
  const parts = [];
  const effPct = r.commission_base ? (r.commission / r.commission_base) * 100 : null;
  // A channel with no rates entered in Profit Settings prices every charge at
  // ₹0 — say so, rather than let "0% commission" read like a real rate.
  if (!r.commission && !r.gt && !r.fixed_fee && !r.freight && !r.reverse_logistics && !r.commission_refund) {
    return "No rates set up in Profit Settings for this channel yet — its charges are counted as ₹0.";
  }
  if (b.mirrors) {
    parts.push(`Flipkart priced with ${PROFIT_CH_LABELS[b.mirrors] || b.mirrors}'s rates (switch in Profit Settings)`);
  }
  if (b.model === "tiered") {
    parts.push(`Commission ${effPct != null ? `${effPct.toFixed(1)}%` : "—"} of (${b.commission_base_label}), tiered by price band (category level ${b.level})`);
    if (r.sale_qty) {
      parts.push(`GT ₹${n2c(r.gt / r.sale_qty)}/pc`);
      parts.push(`Fixed fee ₹${n2c(r.fixed_fee / r.sale_qty)}/pc`);
    }
    if (r.return_qty) parts.push(`Rev. logistics ₹${n2c(r.reverse_logistics / r.return_qty)}/return (zone-weighted)`);
  } else {
    parts.push(b.model === "flat"
      ? `Commission ₹${n2c(b.commission_flat)}/pc flat`
      : `Commission ${b.commission_pct}% of ${b.commission_base_label}`);
    const freight = b.freight_type === "pct" ? (b.freight_pct ? `${b.freight_pct}% of SP` : null) : (b.freight_flat ? `₹${n2c(b.freight_flat)}/pc` : null);
    parts.push(freight ? `Freight ${freight}, ${b.freight_on_return ? "charged again on returns" : "not charged on returns"}` : "No freight charge");
  }
  if (r.sale_qty) {
    parts.push(`Avg SP ₹${n0c(r.sale_value_incl_gst / r.sale_qty)} incl. ${b.gst_pct != null ? `${b.gst_pct}%` : "5%/18%"} GST`);
  }
  return parts.join(" · ");
}

// Hover breakdown for a Weekly Trend row's "Profit (after comm.)" — shows, per
// marketplace, what was charged on units sold (forward) and what happened on
// units returned (commission refunded, reverse logistics added), and how that
// nets down from GP to the profit figure. Amounts come straight from the
// server's charges_breakdown (see server.js addCommissionAdjustedProfit).
// onClose set = pinned by a click (2026-10-17), same behaviour as CpProfitTooltip.
export function WeeklyChargesTooltip({ tip, onClose }) {
  const ref = useRef();
  const tableRef = useRef();
  const dims = useRef({ w: 820, h: 300 });
  // 2026-10-24 (owner): never scroll sideways, even on a tablet at 125% zoom - when the 16-column table is wider than the
  // screen the whole box is scaled down (CSS zoom) to fit. Re-measured on window resize / zoom change.
  const [fit, setFit] = useState(1);
  const [, bump] = useState(0);
  const [size, setSize] = useState(null); // box size in its own (unzoomed) px, for centring
  useLayoutEffect(() => {
    if (ref.current) { const w = ref.current.offsetWidth, h = ref.current.offsetHeight; setSize((o) => (o && o.w === w && o.h === h ? o : { w, h })); }
  });
  useLayoutEffect(() => {
    const measure = () => {
      const t = tableRef.current;
      if (!t) return;
      const need = t.scrollWidth + 30; // + box padding/border
      const avail = window.innerWidth - 16;
      setFit((f) => { const z = Math.min(1, avail / (need / f)); return Math.abs(z - f) > 0.01 ? z : f; });
    };
    const onResize = () => { measure(); bump((n) => n + 1); }; // re-centre too
    measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [tip.breakdown]);
  useEffect(() => { if (ref.current) dims.current = { w: ref.current.offsetWidth * fit, h: ref.current.offsetHeight * fit }; });
  const OFFSET = 18;
  const vw = window.innerWidth, vh = window.innerHeight;
  const { w, h } = dims.current;
  const left = tip.x + OFFSET + w <= vw ? tip.x + OFFSET : tip.x - w - OFFSET;
  const top = tip.y + OFFSET + h <= vh ? tip.y + OFFSET : tip.y - h - OFFSET;
  const safeLeft = Math.max(4, Math.min(left, vw - w - 4));
  const safeTop = Math.max(4, Math.min(top, vh - h - 4));
  // 2026-10-20: a pinned box can be dragged by its title bar (at 110%+ zoom it did not fit beside the click point).
  const [drag, setDrag] = useState(null); // { left, top } once moved
  const startDrag = (e) => {
    if (!onClose || e.button !== 0) return;
    e.preventDefault();
    const box = ref.current.getBoundingClientRect();
    const dx = e.clientX - box.left, dy = e.clientY - box.top;
    const move = (ev) => setDrag({
      left: Math.max(4 - box.width + 60, Math.min(ev.clientX - dx, window.innerWidth - 60)) / fit,
      top: Math.max(4, Math.min(ev.clientY - dy, window.innerHeight - 40)) / fit,
    });
    const up = () => { document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up); };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  const rows = tip.breakdown || [];
  const cell = { padding: "4px 10px", border: "1px solid var(--border)", fontSize: 12, whiteSpace: "nowrap", textAlign: "right", verticalAlign: "top" };
  const head = { ...cell, fontWeight: 700, color: "var(--text-muted)", background: "var(--bg-card)" };
  const tot = { ...cell, fontWeight: 700 };
  // The Total row is built the same shape as a channel row (every numeric
  // field summed), so both render through channelCells below.
  const totalRow = {};
  for (const r of rows) for (const [k, v] of Object.entries(r)) if (typeof v === "number") totalRow[k] = (totalRow[k] || 0) + v;
  // 2026-10-27 (owner): Ginesys books Myntra/Flipkart sales NET of GT, so Sold / Gross profit / Net sales already have GT taken
  // out and GT is NOT part of Forward total / Net charges (no double deduction). "With GT" (the shared switch in Style profit)
  // adds GT back to Sold, Net sales and Gross profit AND lists it as a charge, so Profit is identical either way.
  const withGt = !!tip.withGt;
  const gtOn = (r) => (withGt ? (r.gt || 0) : 0);
  const net = (totalRow.net || 0) + gtOn(totalRow);

  // Charges show as −₹, the commission refund as +₹ (it comes back), and a
  // charge a channel doesn't have (e.g. GT/fixed fee outside Myntra/Flipkart)
  // as "—". Under each amount: the rate and what it was applied to, so every
  // figure can be checked by hand.
  const charge = (v) => (v ? `−₹${n0c(v)}` : "—");
  const credit = (v) => (v ? `+₹${n0c(v)}` : "—");
  const pct = (v, den) => (den > 0 ? `${((v / den) * 100).toFixed(1)}%` : "—");
  const td = (style, main, sub, color) => (
    <td style={{ ...style, color }}>
      {main}
      {sub && <div style={{ fontSize: 10, fontWeight: 400, color: "var(--text-muted)" }}>{sub}</div>}
    </td>
  );
  // A flat per-piece charge: "₹230 × 14 · 6.5% of sales".
  const perPiece = (amt, qty, salesValue) => (amt && qty ? `₹${n2c(amt / qty)} × ${qty} · ${pct(amt, salesValue)} of sales` : null);
  const channelCells = (r, style) => {
    const g = gtOn(r);
    // GT is on the pieces KEPT: GT of the units sold minus GT of the units returned (the returned unit was booked net of GT too).
    // Sold and Returned each get their own GT so "Sold - Returned = Net sales" reads true in both modes (Power BI Custom: net + GT per kept piece).
    const gtSold = withGt ? (r.gt_sold != null ? r.gt_sold : r.gt || 0) : 0, gtRet = withGt ? (r.gt_returned || 0) : 0;
    const saleValue = (r.sale_value || 0) + gtSold, retValue = (r.return_value || 0) + gtRet;
    const gp = (r.gp || 0) + g, netCharges = (r.net || 0) + g, forward = (r.forward_total || 0) + g;
    const netSales = saleValue - retValue;
    return (
      <>
        {td(style, r.sale_qty, saleValue ? `₹${n0c(saleValue)}` : null)}
        {td(style, charge(r.commission), r.commission ? `${pct(r.commission, r.commission_base)} of ₹${n0c(r.commission_base)}` : null)}
        {withGt ? td(style, charge(r.gt), perPiece(r.gt, (r.sale_qty || 0) - (r.return_qty || 0), netSales)?.replace(" of sales", " of net sales"))
          : td(style, r.gt ? `₹${n0c(r.gt)}` : "—", r.gt ? "already deducted by Ginesys" : null, "var(--text-muted)")}
        {td(style, charge(r.fixed_fee), perPiece(r.fixed_fee, r.sale_qty, saleValue))}
        {td(style, charge(r.freight), perPiece(r.freight, r.sale_qty, saleValue))}
        {td(style, charge(forward), forward ? `${pct(forward, saleValue)} of sales` : null)}
        {/* 2026-10-20: return % = returned pcs ÷ sold pcs (same basis as the weekly table's Return %). */}
        {td(style, r.return_qty, r.sale_qty ? `${((r.return_qty || 0) / r.sale_qty * 100).toFixed(1)}% returned${retValue ? ` · ₹${n0c(retValue)}` : ""}` : (retValue ? `₹${n0c(retValue)}` : null))}
        {td(style, credit(r.commission_refund),
          r.commission_refund ? `${pct(r.commission_refund, r.commission_refund_base)} of ₹${n0c(r.commission_refund_base)}` : null,
          r.commission_refund ? "var(--success)" : undefined)}
        {td(style, charge(r.reverse_logistics), r.reverse_logistics && r.return_qty ? `₹${n2c(r.reverse_logistics / r.return_qty)} × ${r.return_qty}` : null)}
        {td(style, `₹${n0c(netSales)}`, retValue ? `₹${n0c(saleValue)} − ₹${n0c(retValue)}` : null)}
        {/* Cost = net sales − GP, so the row reads Net sales − Cost = Gross profit. */}
        {td(style, `−₹${n0c(netSales - gp)}`, netSales > 0 ? `${pct(netSales - gp, netSales)} of net sales` : null)}
        {td({ ...style, fontWeight: 700 }, `₹${n0c(gp)}`, netSales > 0 ? `${pct(gp, netSales)} of net sales` : null, gp < 0 ? "var(--danger)" : undefined)}
        {/* Net charges are normally a cost; they go negative when a channel's
            commission refund outweighs its charges that week — shown as a green
            +₹ credit. */}
        {td({ ...style, fontWeight: 700 }, netCharges < 0 ? `+₹${n0c(-netCharges)}` : `−₹${n0c(netCharges)}`,
          netSales > 0 ? `${pct(netCharges, netSales)} of net sales` : null,
          netCharges < 0 ? "var(--success)" : undefined)}
        {td({ ...style, fontWeight: 700 }, `₹${n0c(r.profit)}`, netSales > 0 ? `${pct(r.profit, netSales)} of net sales` : null,
          r.profit >= 0 ? "var(--success)" : "var(--danger)")}
        {/* 2026-10-17 Profit/pc = profit ÷ net pieces kept (sold − returned), same basis as the Weekly Trend "/pc net". */}
        {(() => {
          const netPcs = (r.sale_qty || 0) - (r.return_qty || 0);
          return netPcs > 0
            ? td({ ...style, fontWeight: 700 }, `₹${n0c(r.profit / netPcs)}`, `÷ ${netPcs} net pc${netPcs === 1 ? "" : "s"}`, r.profit >= 0 ? "var(--success)" : "var(--danger)")
            : td(style, "—", "no net pcs");
        })()}
      </>
    );
  };

  return (
    // Pinned (clicked) = centred on screen until dragged (owner 2026-10-24: it opened half off-screen). Hover = follows the
    // cursor. Positions are divided by `fit` because CSS zoom scales left/top too.
    <div ref={ref} data-profit-pin="box" style={{ position: "fixed", zIndex: 9999, pointerEvents: onClose ? "auto" : "none", zoom: fit,
      ...(drag ? { left: drag.left, top: drag.top }
        : onClose ? { left: Math.max(8 / fit, vw / fit / 2 - (size ? size.w : 0) / 2), top: Math.max(8 / fit, vh / fit / 2 - (size ? size.h : 0) / 2), visibility: size ? "visible" : "hidden" }
        : { left: safeLeft / fit, top: safeTop / fit }),
      maxWidth: `calc((100vw - 16px) / ${fit})`, maxHeight: `calc((100vh - 16px) / ${fit})`, overflowY: "auto", overflowX: "hidden", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, padding: "12px 14px", boxShadow: "var(--shadow-md)" }}>
      {onClose && (
        <button onClick={onClose} aria-label="Close" title="Close (Esc)"
          style={{ position: "absolute", top: 6, right: 8, background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 2 }}>✕</button>
      )}
      <div onMouseDown={startDrag} title={onClose ? "Drag to move" : undefined}
        style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, paddingRight: onClose ? 22 : 0, color: "var(--text)", cursor: onClose ? "move" : "default", userSelect: "none" }}>
        {onClose && <span aria-hidden="true" style={{ color: "var(--text-muted)", marginRight: 6 }}>⠿</span>}
        Marketplace charges — {tip.title}
      </div>
      <table ref={tableRef} style={{ borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...head, textAlign: "left" }}>Channel</th>
            <th style={head} title={withGt ? "Sales value excl. GST with GT added back (Power BI 'Sales Value (Custom)')." : "Sales value excl. GST as booked in Ginesys - already after the GT charge (Power BI plain 'Sales Value')."}>{withGt ? "Sold (incl. GT)" : "Sold (after GT)"}</th>
            <th style={head}>Commission</th>
            <th style={head} title={withGt ? "GT charge, deducted once here." : "Ginesys books Myntra/Flipkart sales after the GT charge, so it is not deducted again."}>{withGt ? "GT" : "GT (info)"}</th>
            <th style={head}>Fixed fee</th>
            <th style={head}>Freight</th>
            <th style={head}>Forward total</th>
            <th style={head}>Returned</th>
            <th style={head}>Comm. refund</th>
            <th style={head}>Rev. logistics</th>
            <th style={head}>Net sales</th>
            <th style={head}>Cost</th>
            <th style={head}>Gross profit</th>
            <th style={head}>Net charges</th>
            <th style={head}>Profit</th>
            <th style={head}>Profit/pc</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.channel}>
              <td style={{ ...cell, textAlign: "left", fontWeight: 700 }}>{PROFIT_CH_LABELS[r.channel] || r.channel}</td>
              {channelCells(r, cell)}
            </tr>
          ))}
          <tr>
            <td style={{ ...tot, textAlign: "left" }}>Total</td>
            {channelCells(totalRow, tot)}
          </tr>
        </tbody>
      </table>
      <div style={{ marginTop: 10, fontSize: 12, color: "var(--text)" }}>
        Gross profit ₹{n0c(tip.gp + gtOn(totalRow))} − net charges ₹{n0c(net)} ={" "}
        <span style={{ fontWeight: 700, color: tip.profit >= 0 ? "var(--success)" : "var(--danger)" }}>profit ₹{n0c(tip.profit)}</span>.
      </div>
      <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--border)", fontSize: 11, color: "var(--text-muted)" }}>
        <div style={{ fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>Basis of charges</div>
        {rows.filter((r) => r.basis).map((r) => (
          <div key={r.channel} style={{ marginBottom: 3, whiteSpace: "normal" }}>
            <span style={{ fontWeight: 700, color: "var(--text)" }}>{PROFIT_CH_LABELS[r.channel] || r.channel}:</span>{" "}
            {chargeBasisText(r)}
          </div>
        ))}
        <div style={{ marginTop: 6 }}>
          Commission is charged on every sale and refunded on every return, so it lands on net sales.
          Fixed fee and forward freight are not refunded; reverse logistics is added per return.
          {withGt ? " GT is added back to Sold and Gross profit and listed as a charge." : " GT is already out of Sold (Ginesys books it net), so it is shown for information only."}
          Rates are today's Profit Settings.
        </div>
      </div>
    </div>
  );
}

// Avg actual profit (after commission) per week, counted from the style's first selling week in the window so a
// style launched mid-window isn't diluted by its pre-launch zero weeks. null = no sales / no profit figure.
// Also returns ROS = net pcs per selling week over the same weeks, so Wkly Profit = ROS x actual profit per net pc.
export function avgWeeklyProfit(r) {
  const weeks = r && r.settled_view ? ((r && r.weeks) || []).filter((w) => !w.open_returns) : (r && r.weeks) || [];   // settled view: open-return weeks are out of the average
  const t = (r && r.totals) || {};
  const first = weeks.findIndex((w) => (w.gross_sales_qty || 0) > 0);
  if (t.profit_after_commission == null || first < 0) return null;
  const n = weeks.length - first;
  return { profit: t.profit_after_commission / n, ros: (t.net_qty || 0) / n, weeks: n, total: t.profit_after_commission, netQty: t.net_qty || 0 };
}

// Weeks counted by the summaries: settled weeks only when the switch is on (the table itself still lists every week).
export const weeksShown = (d) => ((d && d.weeks) || []).filter((w) => !(d.settled_view && w.open_returns)).length;
// 2026-10-27 (owner, return-window lag): "Settled weeks only" swaps in the server's totals_settled (same shape as totals, computed from weeks
// that ended at least `return_lag_weeks` ago) and the per-colour settled figures. Weeks stay listed. No totals_settled (old/odd result) = unchanged.
export function settledView(d) {
  if (!d || !d.totals_settled) return d;
  const variant_summary = d.variant_summary ? d.variant_summary.map((v) => (v.totals_settled ? { ...v, totals: v.totals_settled, gt: v.gt_settled ?? v.gt } : v)) : d.variant_summary;
  return { ...d, settled_view: true, totals: d.totals_settled, variant_summary };
}

// Small shared switch for views that use the common Segmented control (CP Approvals). Insight uses its own pill pair for the same settledView().
export function SettledSwitch({ d, on, onChange }) {
  if (!d || !d.totals_settled) return null;
  const n = d.return_lag_weeks != null ? d.return_lag_weeks : 3;
  return (
    <Segmented size="sm" ariaLabel="Settled weeks" value={on ? "settled" : "all"} onChange={(v) => onChange(v === "settled")}
      options={[{ value: "settled", label: `Settled weeks only (excl. last ${n} wks)`, title: "Totals skip weeks that ended recently, because returns can still arrive" },
        { value: "all", label: "All weeks", title: "Totals include every week, including those with open returns" }]} />
  );
}

// 2026-10-28 (owner): Insight KPI cards with Myntra split out. ONE source: the same per-channel profit / pieces as the "Actual profit by channel"
// tiles (totals.charges_breakdown), the same weeks as avgWeeklyProfit (follows the Settled / All weeks switch; profit is unchanged by the GT switch).
// Returns null when there is no profit figure or the style has no Myntra sales (cards then show "-"). Pure, no I/O.
export function myntraSplit(d) {
  const wk = avgWeeklyProfit(d);
  const t = (d && d.totals) || {};
  const m = ((t.charges_breakdown) || []).find((r) => r.channel === "myntra");
  if (!wk || !m || !(m.sale_qty > 0)) return null;
  const mNet = (m.sale_qty || 0) - (m.return_qty || 0);
  const profitEx = wk.total - (m.profit || 0);
  const netEx = wk.netQty - mNet;
  const stock = t.closing_stock_latest ?? null;
  const cover = (net) => (stock != null && net > 0 ? stock / (net / wk.weeks) : null);
  return { weeks: wk.weeks, myntraProfit: m.profit || 0, myntraNet: mNet, profit: wk.total, netPcs: wk.netQty, profitEx, netEx,
    perWkEx: profitEx / wk.weeks, perPc: wk.netQty > 0 ? wk.total / wk.netQty : null, perPcEx: netEx > 0 ? profitEx / netEx : null,
    stock, cover: cover(wk.netQty), coverEx: cover(netEx) };
}
// Per-piece money: paise below Rs 10 (so a small profit never reads Rs 0), whole rupees otherwise.
export const fmtPerPc = (v) => (v == null ? "—" : Math.abs(v) < 10 ? `₹${v.toFixed(2)}` : `₹${Math.round(v).toLocaleString("en-IN")}`);

const noTip = () => ({});
// "27 Apr" from an ISO date (compact week column; the full date with year is on hover).
const fmtShort = (iso) => { const x = new Date(`${String(iso).slice(0, 10)}T00:00:00`); return isNaN(x) ? fmtDate(iso) : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }); };

// 2026-10-24 (owner): Ginesys books Myntra/Flipkart sales NET of the GT (goods transportation / forward logistics) charge.
// "With GT" adds the GT priced in each week's charges_breakdown back to Sales Value and GP, and re-derives GP% and ASP.
// Profit (after comm.) is unchanged: the server no longer deducts GT (it is already out of the booked value), and the charges popup
// lists it as a charge only in this mode, so GP + GT - GT charge = the same profit. Returns a copy; d itself is not touched.
const gtOf = (row) => (row && row.charges_breakdown ? row.charges_breakdown.reduce((a, r) => a + (r.gt || 0), 0) : 0);
const regp = (row, gt) => {
  if (!gt || row.sales_value == null) return { ...row, gt_added: 0 };
  const sales_value = row.sales_value + gt, gp = row.gp + gt;
  return { ...row, sales_value, gp, gt_added: gt,
    gp_pct: sales_value > 0 && row.net_qty > 0 ? Math.round(gp / sales_value * 1000) / 10 : null,
    asp: row.net_qty ? Math.round(Math.abs(sales_value) / Math.abs(row.net_qty) * 100) / 100 : null };
};
export function withGtAdded(d) {
  if (!d || !Array.isArray(d.weeks)) return d;
  return { ...d, with_gt: true, weeks: d.weeks.map((w) => regp(w, gtOf(w))), totals: d.totals ? regp(d.totals, gtOf(d.totals)) : d.totals };
}

// fullWidth (Insight popup, 2026-10-24): table stretches across the box instead of hugging its content.
// compact (Insight popup, 2026-10-24): tighter cells, wrapping headers, week shown as "27 Apr – 03 May" so the table fits
// beside the photo without a sideways scroll. CP Approvals uses neither and is unchanged.
export default function WeeklyTrendTable({ d, styleNumber, chargesTipHandlers = noTip, fullWidth = false, compact = false }) {
  const L = (full, a, b) => (compact ? <>{a}<br />{b}</> : full); // compact: two-line header
  const weekLabel = (w) => (compact ? `${fmtShort(w.week_start)} – ${fmtShort(w.week_end)}` : `${fmtDate(w.week_start)} – ${fmtDate(w.week_end)}`);
  return (
    <>
      <style>{`.weekly-trend-table th, .weekly-trend-table td { border: 1px solid var(--border); }
        .weekly-trend-table.wt-compact th, .weekly-trend-table.wt-compact td { padding-left: 6px !important; padding-right: 6px !important; }
        .weekly-trend-table.wt-compact th { white-space: normal; vertical-align: bottom; }
        .weekly-trend-table.wt-compact td { white-space: nowrap; }`}</style>
      <table className={`weekly-trend-table${compact ? " wt-compact" : ""}`} style={{ fontSize: 12, width: fullWidth && !compact ? "100%" : "auto", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", padding: "2px 12px 2px 0" }}>Week</th>
            <th style={{ textAlign: "right", padding: "2px 12px" }}>{L("Gross Sales Qty", "Gross", "Sales Qty")}</th>
            <th style={{ textAlign: "right", padding: "2px 12px" }}>{L("Return Qty", "Return", "Qty")}</th>
            <th style={{ textAlign: "right", padding: "2px 12px" }}>{L("Return %", "Return", "%")}</th>
            <th style={{ textAlign: "right", padding: "2px 12px" }}>{L("Net Qty", "Net", "Qty")}</th>
            <th style={{ textAlign: "right", padding: "2px 12px" }} title="E-com WH closing stock at the end of the week (Planning_DB snapshot, main floor only). — = no snapshot or not set up.">{L("Closing stock", "Closing", "stock")}</th>
            <th style={{ textAlign: "right", padding: "2px 12px" }} title="Weeks of cover = closing stock ÷ that week's net qty. Red over 26, amber over 12.">{L("Wks cover", "Wks", "cover")}</th>
            <th style={{ textAlign: "right", padding: "2px 12px" }} title={d.with_gt ? "Includes the GT (forward logistics) charge added back" : undefined}>{compact ? (d.with_gt ? L(null, "Sales Value", "(incl. GT)") : L(null, "Sales", "Value")) : <>Sales Value{d.with_gt ? " (incl. GT)" : ""}</>}</th>
            <th style={{ textAlign: "right", padding: "2px 12px" }}>GP</th>
            <th style={{ textAlign: "right", padding: "2px 12px" }}>GP%</th>
            <th style={{ textAlign: "right", padding: "2px 12px" }} title="GP minus marketplace commission/GT/fixed-fee/reverse-logistics charges, via the same engine as Profit/Pc">{L("Profit (after comm.)", "Profit", "(after comm.)")}</th>
            <th style={{ textAlign: "right", padding: "2px 0" }}>{L("Profit %", "Profit", "%")}</th>
          </tr>
        </thead>
        <tbody>
          {d.weeks.map((w, i) => (
            <tr key={i} style={d.settled_view && w.open_returns ? { opacity: 0.55 } : undefined}>
              <td style={{ padding: "2px 12px 2px 0", whiteSpace: "nowrap", color: "var(--text-muted)" }}>
                <span title={compact ? `${fmtDate(w.week_start)} – ${fmtDate(w.week_end)}` : undefined}>{weekLabel(w)}</span>
                {w.open_returns && <span title={`Ended less than ${d.return_lag_weeks} weeks ago - returns can still arrive${d.settled_view ? ", so this week is left out of the totals" : ""}`} style={{ marginLeft: 4, fontSize: 9.5, fontWeight: 700, padding: "0 4px", border: "1px solid var(--border)", borderRadius: 3, color: "var(--warning, var(--text-muted))" }}>open returns</span>}
                {w.returns_may_still_come && <span title="Returns for this week's sales may still come" style={{ marginLeft: 3 }}>*</span>}
              </td>
              <td style={{ textAlign: "right", padding: "2px 12px" }}>{w.gross_sales_qty}</td>
              <td style={{ textAlign: "right", padding: "2px 12px", color: w.return_qty ? "var(--danger)" : "var(--text-muted)" }}>{w.return_qty}</td>
              <td style={{ textAlign: "right", padding: "2px 12px", color: w.return_pct == null ? "var(--text-muted)" : "var(--danger)" }}>
                {w.return_pct != null ? `${w.return_pct}%` : "—"}
              </td>
              <td style={{ textAlign: "right", padding: "2px 12px", fontWeight: 700 }}>{w.net_qty}</td>
              <td style={{ textAlign: "right", padding: "2px 12px", color: w.closing_stock == null ? "var(--text-muted)" : undefined }}>{w.closing_stock ?? "—"}</td>
              <td style={{ textAlign: "right", padding: "2px 12px", color: coverColor(weeksCover(w.closing_stock, w.net_qty)) }}>{coverText(weeksCover(w.closing_stock, w.net_qty))}</td>
              <td style={{ textAlign: "right", padding: "2px 12px" }}>
                {w.sales_value != null ? `₹${w.sales_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
              </td>
              <td style={{ textAlign: "right", padding: "2px 12px" }}>
                {w.gp != null ? `₹${w.gp.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
              </td>
              <td style={{ textAlign: "right", padding: "2px 12px", color: w.gp_pct == null ? "var(--text-muted)" : w.gp_pct >= 0 ? "var(--success)" : "var(--danger)" }}>
                {w.gp_pct != null ? `${w.gp_pct}%` : "—"}
              </td>
              <td
                style={{ textAlign: "right", padding: "2px 12px", cursor: (w.charges_breakdown || []).length ? "help" : "default", textDecoration: (w.charges_breakdown || []).length ? "underline dotted" : "none" }}
                {...chargesTipHandlers(`${styleNumber} · ${fmtDate(w.week_start)} – ${fmtDate(w.week_end)}`, w)}
              >
                {w.profit_after_commission != null ? `₹${w.profit_after_commission.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
              </td>
              <td style={{ textAlign: "right", padding: "2px 0", fontWeight: 700, color: w.profit_after_commission_pct == null ? "var(--text-muted)" : w.profit_after_commission_pct >= 0 ? "var(--success)" : "var(--danger)" }}>
                {w.profit_after_commission_pct != null ? `${w.profit_after_commission_pct}%` : "—"}
              </td>
            </tr>
          ))}
          <tr style={{ borderTop: "1px solid var(--border)", fontWeight: 700 }}>
            <td style={{ padding: "4px 12px 2px 0" }}>{d.settled_view ? "Total (settled)" : "Total"}</td>
            <td style={{ textAlign: "right", padding: "4px 12px 2px" }}>{d.totals.gross_sales_qty}</td>
            <td style={{ textAlign: "right", padding: "4px 12px 2px" }}>{d.totals.return_qty}</td>
            <td style={{ textAlign: "right", padding: "4px 12px 2px" }}>
              {d.totals.return_pct != null ? `${d.totals.return_pct}%` : "—"}
            </td>
            <td style={{ textAlign: "right", padding: "4px 12px 2px" }}>{d.totals.net_qty}</td>
            <td style={{ textAlign: "right", padding: "4px 12px 2px" }} title={d.totals.closing_stock_week ? `Latest: week of ${fmtDate(d.totals.closing_stock_week)}` : undefined}>{d.totals.closing_stock_latest ?? "—"}</td>
            {(() => {
              const avg = d.weeks.length ? (d.totals.net_qty || 0) / d.weeks.length : 0;
              const c = weeksCover(d.totals.closing_stock_latest, avg);
              return <td style={{ textAlign: "right", padding: "4px 12px 2px", color: coverColor(c) }} title={`Latest closing stock ÷ ${d.weeks.length}-week average net qty per week`}>{coverText(c)}</td>;
            })()}
            <td style={{ textAlign: "right", padding: "4px 12px 2px" }}>
              {d.totals.sales_value != null ? `₹${d.totals.sales_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
            </td>
            <td style={{ textAlign: "right", padding: "4px 12px 2px" }}>
              {d.totals.gp != null ? `₹${d.totals.gp.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
            </td>
            <td style={{ textAlign: "right", padding: "4px 12px 2px", color: d.totals.gp_pct >= 0 ? "var(--success)" : "var(--danger)" }}>
              {d.totals.gp_pct != null ? `${d.totals.gp_pct}%` : "—"}
            </td>
            <td
              style={{ textAlign: "right", padding: "4px 12px 2px", cursor: (d.totals.charges_breakdown || []).length ? "help" : "default", textDecoration: (d.totals.charges_breakdown || []).length ? "underline dotted" : "none" }}
              {...chargesTipHandlers(`${styleNumber} · last ${d.weeks.length} weeks total`, d.totals)}
            >
              {d.totals.profit_after_commission != null ? `₹${d.totals.profit_after_commission.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—"}
              {weeklyTotalActuals(d.totals).perUnit != null && (
                <div style={{ fontSize: 10, fontWeight: 400, color: "var(--text-muted)" }}>
                  ₹{n0c(weeklyTotalActuals(d.totals).perUnit)}/pc net
                </div>
              )}
            </td>
            <td style={{ textAlign: "right", padding: "4px 0 2px", color: d.totals.profit_after_commission_pct >= 0 ? "var(--success)" : "var(--danger)" }}>
              {d.totals.profit_after_commission_pct != null ? `${d.totals.profit_after_commission_pct}%` : "—"}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
}
